/**
 * Summary Cache Service
 * 
 * Provides caching for AI summaries across tools (Geographic Explorer, 
 * Competitive Explorer, Projections Explorer, AI Summary Tool).
 * 
 * Summaries are cached by school UUID + travel mode + drive time combination.
 * When a user moves between tools, cached summaries load instantly.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { getAllStoredSummaries, getUserSummaryData } from '../services/aiService';

// Cache key generator — includes travelMode to prevent walk/drive collisions
const getCacheKey = (schoolUuid, driveTime, travelMode = 'drive') => `${schoolUuid}_${travelMode}_${driveTime}`;

// Create context
const SummaryCacheContext = createContext(null);

/**
 * Summary Cache Provider
 * Wrap your app with this to enable summary caching across all tools.
 */
export function SummaryCacheProvider({ children }) {
  // Main cache: { [schoolUuid_travelMode_driveTime]: { geographic, competitive, projections, combined, data, loadedAt } }
  const [cache, setCache] = useState({});
  
  // Track loading states to prevent duplicate fetches
  const loadingRef = useRef({});
  
  // Cache TTL in milliseconds (1 hour - summaries don't change often)
  const CACHE_TTL = 60 * 60 * 1000;

  /**
   * Check if cached data is still valid
   */
  const isCacheValid = useCallback((cacheEntry) => {
    if (!cacheEntry || !cacheEntry.loadedAt) return false;
    return (Date.now() - cacheEntry.loadedAt) < CACHE_TTL;
  }, [CACHE_TTL]);

  /**
   * Get cached summary for a school/drive time/travel mode combination
   * Returns null if not cached or expired
   */
  const getCachedSummary = useCallback((schoolUuid, driveTime, travelMode = 'drive') => {
    const key = getCacheKey(schoolUuid, driveTime, travelMode);
    const entry = cache[key];
    
    if (entry && isCacheValid(entry)) {
      return entry;
    }
    return null;
  }, [cache, isCacheValid]);

  /**
   * Set summary in cache
   */
  const setCachedSummary = useCallback((schoolUuid, driveTime, summaryData, travelMode = 'drive') => {
    const key = getCacheKey(schoolUuid, driveTime, travelMode);
    setCache(prev => ({
      ...prev,
      [key]: {
        ...summaryData,
        loadedAt: Date.now()
      }
    }));
  }, []);

  /**
   * Update a specific summary type in cache (e.g., just geographic)
   */
  const updateCachedSummaryType = useCallback((schoolUuid, driveTime, summaryType, data, travelMode = 'drive') => {
    const key = getCacheKey(schoolUuid, driveTime, travelMode);
    setCache(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [summaryType]: data,
        loadedAt: Date.now()
      }
    }));
  }, []);

  /**
   * Load and cache summaries for a school
   * TWO-PHASE LOADING for better perceived performance:
   * Phase 1: Load narratives immediately (fast - under 1s) 
   * Phase 2: Load visualization data in background (slower - 1-2min)
   */
  const loadSummaries = useCallback(async (schoolUuid, driveTime, options = {}) => {
    const { forceRefresh = false, travelMode = 'drive' } = options;
    const key = getCacheKey(schoolUuid, driveTime, travelMode);
    
    // Return cached data if valid and not forcing refresh
    if (!forceRefresh) {
      const cached = getCachedSummary(schoolUuid, driveTime, travelMode);
      if (cached) {
        return cached;
      }
    }
    
    // Prevent duplicate fetches
    if (loadingRef.current[key]) {
      // Wait for existing fetch to complete
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!loadingRef.current[key]) {
            clearInterval(checkInterval);
            resolve(getCachedSummary(schoolUuid, driveTime, travelMode));
          }
        }, 100);
      });
    }
    
    loadingRef.current[key] = true;
    
    try {
      // PHASE 1: Load narratives FIRST (fast!)
      const summariesResult = await getAllStoredSummaries(schoolUuid, driveTime, travelMode).catch(() => ({ summaries: [] }));
      
      // Build initial cache entry with narratives only
      const cacheEntry = {
        loadedAt: Date.now(),
        data: null,
        dataLoading: true, // Flag to indicate data is still loading
        data_sources: {}
      };
      
      // Process all summaries from batch response
      if (summariesResult.summaries && Array.isArray(summariesResult.summaries)) {
        summariesResult.summaries.forEach(summary => {
          const type = summary.summary_type;
          if (type) {
            cacheEntry[type] = {
              narrative: summary.full_narrative,
              executive_summary: summary.executive_summary,
              sections: summary.sections,
              tokens_used: summary.tokens_used,
              generated_at: summary.generated_at,
              from_cache: true,
              mode: 'stored',
              data: null, // No visualization data yet
              dataLoading: true // Flag for this specific type
            };
          }
        });
      }
      
      // Store narratives in cache IMMEDIATELY so users can start reading
      setCache(prev => ({
        ...prev,
        [key]: cacheEntry
      }));
      
      // Mark narrative loading as complete
      loadingRef.current[key] = false;
      
      // PHASE 2: Load visualization data in background (slower, non-blocking)
      // Don't await this - let it run in the background
      getUserSummaryData(schoolUuid, driveTime, ['geographic', 'competitive', 'projections'], travelMode)
        .then(rawData => {
          const updatedEntry = {
            ...cacheEntry,
            loadedAt: Date.now(),
            data: rawData,
            dataLoading: false,
            data_sources: rawData.data_sources || {}
          };
          
          // Add visualization data to existing narratives
          if (summariesResult.summaries && Array.isArray(summariesResult.summaries)) {
            summariesResult.summaries.forEach(summary => {
              const type = summary.summary_type;
              if (type && updatedEntry[type]) {
                updatedEntry[type] = {
                  ...updatedEntry[type],
                  data: rawData[type] || null,
                  dataLoading: false,
                  data_sources: rawData.data_sources || {}
                };
              }
            });
          }
          
          // Also create entries for types that have raw data but no narrative
          ['geographic', 'competitive', 'projections'].forEach(type => {
            if (!updatedEntry[type] && rawData[type]) {
              updatedEntry[type] = {
                data: rawData[type],
                data_sources: rawData.data_sources || {},
                narrative: null,
                from_cache: true,
                mode: 'data_only',
                dataLoading: false
              };
            }
          });
          
          // Update cache with complete data
          setCache(prev => ({
            ...prev,
            [key]: updatedEntry
          }));
        })
        .catch(error => {
          console.error('Error loading visualization data:', error);
          // Update cache to mark data loading as failed
          setCache(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              dataLoading: false,
              dataError: true
            }
          }));
        });
      
      // Return the narrative-only cache entry immediately
      return cacheEntry;
    } catch (error) {
      console.error('Error loading summaries:', error);
      loadingRef.current[key] = false;
      return null;
    }
  }, [getCachedSummary]);

  /**
   * Preload summaries in the background (non-blocking)
   * Call this when user selects a school to warm the cache
   */
  const preloadSummaries = useCallback((schoolUuid, driveTime, travelMode = 'drive') => {
    const cached = getCachedSummary(schoolUuid, driveTime, travelMode);
    if (!cached) {
      // Load in background without blocking
      loadSummaries(schoolUuid, driveTime, { travelMode }).catch(() => {
        // Silently fail - preloading is optional
      });
    }
  }, [getCachedSummary, loadSummaries]);

  /**
   * Clear cache for a specific school (e.g., after regenerating summaries)
   */
  const clearCache = useCallback((schoolUuid, driveTime = null, travelMode = null) => {
    if (driveTime && travelMode) {
      const key = getCacheKey(schoolUuid, driveTime, travelMode);
      setCache(prev => {
        const newCache = { ...prev };
        delete newCache[key];
        return newCache;
      });
    } else {
      // Clear all drive times / travel modes for this school
      setCache(prev => {
        const newCache = { ...prev };
        Object.keys(newCache).forEach(key => {
          if (key.startsWith(schoolUuid)) {
            delete newCache[key];
          }
        });
        return newCache;
      });
    }
  }, []);

  /**
   * Clear entire cache (e.g., on logout)
   */
  const clearAllCache = useCallback(() => {
    setCache({});
  }, []);

  /**
   * Check if summaries are currently loading (narratives)
   */
  const isLoading = useCallback((schoolUuid, driveTime, travelMode = 'drive') => {
    const key = getCacheKey(schoolUuid, driveTime, travelMode);
    return !!loadingRef.current[key];
  }, []);

  /**
   * Check if visualization data is currently loading
   */
  const isDataLoading = useCallback((schoolUuid, driveTime, travelMode = 'drive') => {
    const cached = getCachedSummary(schoolUuid, driveTime, travelMode);
    return cached?.dataLoading === true;
  }, [getCachedSummary]);

  const value = {
    // Core methods
    getCachedSummary,
    setCachedSummary,
    updateCachedSummaryType,
    loadSummaries,
    preloadSummaries,
    clearCache,
    clearAllCache,
    isLoading,
    isDataLoading,
    
    // Direct cache access (for debugging)
    cache
  };

  return (
    <SummaryCacheContext.Provider value={value}>
      {children}
    </SummaryCacheContext.Provider>
  );
}

/**
 * Hook to access the summary cache
 * Must be used within a SummaryCacheProvider
 */
export function useSummaryCache() {
  const context = useContext(SummaryCacheContext);
  if (!context) {
    throw new Error('useSummaryCache must be used within a SummaryCacheProvider');
  }
  return context;
}

/**
 * Hook to get summaries for a specific school with automatic loading
 * Provides a simpler interface for components that just need the data
 */
export function useSchoolSummaries(schoolUuid, driveTime, options = {}) {
  const { autoLoad = true, travelMode = 'drive' } = options;
  const { getCachedSummary, loadSummaries, isLoading } = useSummaryCache();
  const [summaries, setSummaries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (!schoolUuid || !driveTime) {
      setSummaries(null);
      return;
    }

    // Check cache first
    const cached = getCachedSummary(schoolUuid, driveTime, travelMode);
    if (cached) {
      setSummaries(cached);
      return;
    }

    // Auto-load if enabled
    if (autoLoad && !isLoading(schoolUuid, driveTime, travelMode)) {
      setLoading(true);
      setError(null);
      
      loadSummaries(schoolUuid, driveTime, { travelMode })
        .then(result => {
          setSummaries(result);
        })
        .catch(err => {
          setError(err.message || 'Failed to load summaries');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [schoolUuid, driveTime, travelMode, autoLoad, getCachedSummary, loadSummaries, isLoading]);

  return {
    summaries,
    loading,
    error,
    geographic: summaries?.geographic || null,
    competitive: summaries?.competitive || null,
    projections: summaries?.projections || null,
    combined: summaries?.combined || null,
    data: summaries?.data || null,
    hasSummaries: !!(summaries?.geographic || summaries?.competitive || summaries?.projections)
  };
}

export default useSummaryCache;
